/*
 * Purpose: Immutable mission dossier — append-only audit file (AI reads as dropbox)
 * Last modified: 2026-06-28 | Agent
 * Completeness: 98
 */
import { create } from 'zustand';
import type { MissionDossierActor, MissionDossierEntry, MissionDossierKind } from '@/services/mission-dossier/types';
import { sanitizeDossierPayload } from '@/services/mission-dossier/sanitizePayload';
import { formatMissionDossierForAi } from '@/services/mission-dossier/formatForAi';
import { appendMissionDossierOnServer } from '@/services/mission-dossier/syncApi';
import { bindMissionTaskReceiptSession } from '@/stores/missionTaskReceiptStore';

const STORAGE_PREFIX = 'autoblocks:mission-dossier:';

type DossierBlob = {
  entries: MissionDossierEntry[];
  nextSeq: number;
};

function loadBlob(missionSessionId: string): DossierBlob {
  if (typeof localStorage === 'undefined' || !missionSessionId) {
    return { entries: [], nextSeq: 1 };
  }
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${missionSessionId}`);
    if (!raw) return { entries: [], nextSeq: 1 };
    const parsed = JSON.parse(raw) as DossierBlob;
    const entries = Array.isArray(parsed.entries) ? parsed.entries : [];
    const maxSeq = entries.reduce((m, e) => Math.max(m, e.seq ?? 0), 0);
    return {
      entries,
      nextSeq: parsed.nextSeq ?? maxSeq + 1,
    };
  } catch {
    return { entries: [], nextSeq: 1 };
  }
}

function persistBlob(missionSessionId: string, blob: DossierBlob) {
  if (typeof localStorage === 'undefined' || !missionSessionId) return;
  localStorage.setItem(`${STORAGE_PREFIX}${missionSessionId}`, JSON.stringify(blob));
}

export type RecordMissionDossierInput = {
  actor: MissionDossierActor;
  kind: MissionDossierKind;
  summary: string;
  reasoning?: string;
  action?: string;
  payload?: Record<string, unknown>;
};

interface MissionDossierStore {
  missionSessionId: string;
  entries: MissionDossierEntry[];
  nextSeq: number;
  lastSyncError: string | null;
  setMissionSession: (id: string) => void;
  /** Append-only — the only way to add dossier records */
  append: (input: RecordMissionDossierInput) => MissionDossierEntry | null;
  getAiContext: () => string | undefined;
  syncPendingToServer: () => Promise<void>;
}

let syncTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleSync(get: () => MissionDossierStore) {
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    void get().syncPendingToServer();
  }, 800);
}

export const useMissionDossierStore = create<MissionDossierStore>((set, get) => ({
  missionSessionId: '',
  entries: [],
  nextSeq: 1,
  lastSyncError: null,

  setMissionSession: (id) => {
    const blob = loadBlob(id);
    set({
      missionSessionId: id,
      entries: blob.entries,
      nextSeq: blob.nextSeq,
      lastSyncError: null,
    });
  },

  append: (input) => {
    const { missionSessionId, entries, nextSeq } = get();
    if (!missionSessionId || !input.summary.trim()) return null;

    const entry: MissionDossierEntry = {
      seq: nextSeq,
      id: `md-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
      missionSessionId,
      at: new Date().toISOString(),
      actor: input.actor,
      kind: input.kind,
      summary: input.summary.trim(),
      reasoning: input.reasoning?.trim() || undefined,
      action: input.action?.trim() || undefined,
      payload: sanitizeDossierPayload(input.payload),
    };

    const nextEntries = Object.freeze([...entries, entry]) as MissionDossierEntry[];
    const blob: DossierBlob = { entries: [...nextEntries], nextSeq: nextSeq + 1 };
    persistBlob(missionSessionId, blob);
    set({ entries: nextEntries, nextSeq: nextSeq + 1 });
    scheduleSync(get);
    return entry;
  },

  getAiContext: () => formatMissionDossierForAi(get().entries),

  syncPendingToServer: async () => {
    const { missionSessionId, entries } = get();
    if (!missionSessionId) return;

    const pending = entries.filter((e) => !e.syncedAt);
    if (pending.length === 0) return;

    const result = await appendMissionDossierOnServer(missionSessionId, pending);
    if (!result.ok) {
      set({ lastSyncError: result.message ?? 'Sync failed' });
      return;
    }

    const now = new Date().toISOString();
    const syncedIds = new Set(pending.map((e) => e.id));
    const nextEntries = entries.map((e) =>
      syncedIds.has(e.id) ? { ...e, syncedAt: now } : e
    );
    persistBlob(missionSessionId, { entries: nextEntries, nextSeq: get().nextSeq });
    set({ entries: nextEntries, lastSyncError: null });
  },
}));

export function missionDossierAiContext(): string | undefined {
  return useMissionDossierStore.getState().getAiContext();
}

export function bindMissionDossierSession(missionSessionId: string): void {
  useMissionDossierStore.getState().setMissionSession(missionSessionId);
  bindMissionTaskReceiptSession(missionSessionId);
}
